import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import RouteMetaManager from './components/RouteMetaManager.jsx';

/**
 * Protects printable routes — only accessible when a seat-checker
 * access code is present in localStorage (i.e. the user has logged in
 * through the Seat Checker page with a valid code).
 */
function PrintableGuard({ children }) {
  const hasCode = Boolean(localStorage.getItem('seat_checker_access_code'));
  if (!hasCode) return <Navigate to="/seat-checker" replace />;
  return children;
}
import ScrollToTop from './components/ScrollToTop.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import Layout from './components/Layout.jsx';
import Home from './pages/Home.jsx';
import Invitations from './pages/Invitations.jsx';
import InvitationCategory from './pages/InvitationCategory.jsx';
import DigitalInvitationFaq from './pages/DigitalInvitationFaq.jsx';
import Book from './pages/Book.jsx';
import About from './pages/About.jsx';
import NotFound from './pages/NotFound.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import Contact from './pages/Contact.jsx';
import Pricing from './pages/Pricing.jsx';
import EchoesPromo from './pages/EchoesPromo.jsx';
import SeatChecker from './pages/SeatChecker.jsx';
import AdminSeatChecker from './pages/AdminSeatChecker.jsx';
import AdminInvitationEditor from './pages/AdminInvitationEditor.jsx';
import CustomDesignInvitation from './pages/invitations/CustomDesignInvitation.jsx';
import WeddingInvitation from './pages/invitations/WeddingInvitation.jsx';
import ClassicElegance from './pages/wedding/templates/ClassicElegance.jsx';
import ModernRomance from './pages/wedding/templates/ModernRomance.jsx';
import RusticGarden from './pages/wedding/templates/RusticGarden.jsx';
import KidsPartyFun from './pages/birthday/templates/KidsPartyFun.jsx';
import ElegantMilestone from './pages/birthday/templates/ElegantMilestone.jsx';
import FiestaTheme from './pages/birthday/templates/FiestaTheme.jsx';
import AngelicWhite from './pages/christening/templates/AngelicWhite.jsx';
import BlessedJourney from './pages/christening/templates/BlessedJourney.jsx';
import ProfessionalBlue from './pages/corporate/templates/ProfessionalBlue.jsx';
import ExecutiveGold from './pages/corporate/templates/ExecutiveGold.jsx';
import SilverAnniversary from './pages/anniversary/templates/SilverAnniversary.jsx';
import GoldenYears from './pages/anniversary/templates/GoldenYears.jsx';
import LeonCamping from './pages/invitations/designs/LeonCamping.jsx';
import LhyanneDebut from './pages/invitations/designs/LhyanneDebut.jsx';
import AlexaFionaDebut from './pages/invitations/designs/AlexaFionaDebut.jsx';
import JenineCharisseBirthday from './pages/birthday/templates/JenineCharisseBirthday.jsx';
import JenineCharissePrintable from './pages/printables/JenineCharissePrintable.jsx';
import SofiaAlexandraPrintable from './pages/printables/SofiaAlexandraPrintable.jsx';
import Review from './pages/Review.jsx';
import KaylinAnnBirthday from './pages/birthday/templates/KaylinAnnBirthday.jsx';
import './styles/App.css';

function LegacyAdminInvitationEditorRedirect() {
  const { invitationId } = useParams();

  return <Navigate to={`/admin/invitations/edit/${encodeURIComponent(invitationId || '')}`} replace />;
}

function App() {
  return (
    <Router>
      <RouteMetaManager />
      <ScrollToTop />
      <ErrorBoundary>
        <Routes>
        {/* E-Invitation Display Routes (No Header/Footer) */}
        <Route path="/wedding/:designType/:coupleNames" element={<WeddingInvitation />} />
        <Route path="/wedding/templates/classic-elegance" element={<ClassicElegance />} />
        <Route path="/wedding/templates/classic-elegance/:coupleNames" element={<ClassicElegance />} />
        <Route path="/wedding/templates/modern-romance" element={<ModernRomance />} />
        <Route path="/wedding/templates/modern-romance/:coupleNames" element={<ModernRomance />} />
        <Route path="/wedding/templates/rustic-garden" element={<RusticGarden />} />
        <Route path="/wedding/templates/rustic-garden/:coupleNames" element={<RusticGarden />} />
        <Route path="/birthday/templates/kids-party-fun" element={<KidsPartyFun />} />
        <Route path="/birthday/templates/kids-party-fun/:celebrantName" element={<KidsPartyFun />} />
        <Route path="/birthday/templates/elegant-milestone" element={<ElegantMilestone />} />
        <Route path="/birthday/templates/elegant-milestone/:celebrantName" element={<ElegantMilestone />} />
        <Route path="/birthday/templates/fiesta-theme" element={<FiestaTheme />} />
        <Route path="/birthday/templates/fiesta-theme/:celebrantName" element={<FiestaTheme />} />
        <Route path="/christening/templates/angelic-white" element={<AngelicWhite />} />
        <Route path="/christening/templates/angelic-white/:childName" element={<AngelicWhite />} />
        <Route path="/christening/templates/blessed-journey" element={<BlessedJourney />} />
        <Route path="/christening/templates/blessed-journey/:childName" element={<BlessedJourney />} />
        <Route path="/corporate/templates/professional-blue" element={<ProfessionalBlue />} />
        <Route path="/corporate/templates/professional-blue/:eventName" element={<ProfessionalBlue />} />
        <Route path="/corporate/templates/executive-gold" element={<ExecutiveGold />} />
        <Route path="/corporate/templates/executive-gold/:eventName" element={<ExecutiveGold />} />
        <Route path="/anniversary/templates/silver-anniversary" element={<SilverAnniversary />} />
        <Route path="/anniversary/templates/silver-anniversary/:coupleNames" element={<SilverAnniversary />} />
        <Route path="/anniversary/templates/golden-years" element={<GoldenYears />} />
        <Route path="/anniversary/templates/golden-years/:coupleNames" element={<GoldenYears />} />
        
        {/* Custom Bespoke Invitations */}
        <Route path="/birthday-christening/camping/n.a-leon-villamor-fortuna" element={<LeonCamping />} />
        <Route path="/debut/lhyanne-frankie" element={<LhyanneDebut />} />
        <Route path="/debut/alexa-fiona-colapo" element={<AlexaFionaDebut />} />
        <Route path="/debut/jenine-charisse" element={<JenineCharisseBirthday />} />
        <Route path="/debut/jenine-charisse/printable" element={<PrintableGuard><JenineCharissePrintable /></PrintableGuard>} />
        <Route path="/invitations/custom/sofia-alexandra-debut/printable" element={<PrintableGuard><SofiaAlexandraPrintable /></PrintableGuard>} />
        <Route path="/birthday/kaylin-ann-dela-cruz" element={<KaylinAnnBirthday />} />
        <Route path="/promotion/echoes-of-unforgotten" element={<EchoesPromo />} />
        <Route path="/invitations/custom/:invitationId" element={<CustomDesignInvitation />} />
        <Route path="/seat-checker" element={<SeatChecker />} />
        <Route path="/seat-checker/:invitationId" element={<Navigate to="/seat-checker" replace />} />
        <Route path="/review" element={<Review />} />
        <Route path="/admin/invitations" element={<AdminSeatChecker />} />
        <Route path="/admin/invitations/edit/:invitationId" element={<AdminInvitationEditor />} />
        <Route path="/admin/seat-checker" element={<Navigate to="/admin/invitations" replace />} />
        <Route path="/admin/seat-checker/edit/:invitationId" element={<LegacyAdminInvitationEditorRedirect />} />

        {/* Main Website Routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="invitations" element={<Invitations />} />
          <Route path="digital-invitation-faq" element={<DigitalInvitationFaq />} />
          <Route path="wedding" element={<InvitationCategory categoryKey="wedding" />} />
          <Route path="birthday" element={<InvitationCategory categoryKey="birthday" />} />
          <Route path="christening" element={<InvitationCategory categoryKey="christening" />} />
          <Route path="corporate" element={<InvitationCategory categoryKey="corporate" />} />
          <Route path="anniversary" element={<InvitationCategory categoryKey="anniversary" />} />
          <Route path="book" element={<Book />} />
          <Route path="about" element={<About />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="contact" element={<Contact />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        </Routes>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
